import React from 'react';
import styled from 'styled-components';
import { useFormikContext } from 'formik';
import { Button } from '@avangard/ui/core';
import { ArrowBack, ArrowForward } from '@material-ui/icons';

import { STEPS_ORDER } from '@modules/students/requests';
import { StudentOnlineApplicationStep } from '@modules/types/graphql';

import type { UpdateStudentOnlineApplicationFormValues } from '@modules/students/requests';

const LAYOUT_ID = 'online-application-layout';

const arrowBackIcon = <ArrowBack style={{ fontSize: 16 }} />;
const arrowForwardIcon = <ArrowForward style={{ fontSize: 16 }} />;

const StudentOnlineApplicationFooter = () => {
    const { values, isSubmitting, setFieldValue, validateForm, submitForm } =
        useFormikContext<UpdateStudentOnlineApplicationFormValues>();

    const handleScrollToTop = () => {
        const layoutElement = document.getElementById(LAYOUT_ID);

        if (layoutElement) {
            layoutElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrev = async () => {
        const valid = Object.keys(await validateForm()).length === 0;

        if (!valid) {
            return;
        }

        const currentStepOrderIdx = STEPS_ORDER.findIndex(step => values.currentStep === step);
        const actualStep = STEPS_ORDER[currentStepOrderIdx - 1];

        setFieldValue('temporaryStep', actualStep);

        await submitForm();

        setFieldValue('currentStep', actualStep);

        handleScrollToTop();
    };

    const handleNext = async () => {
        const valid = Object.keys(await validateForm()).length === 0;

        if (!valid) {
            return;
        }

        const currentStepOrderIdx = STEPS_ORDER.findIndex(step => values.currentStep === step);
        const actualStep = STEPS_ORDER[currentStepOrderIdx + 1];

        setFieldValue('temporaryStep', actualStep);

        await submitForm();

        const isLast = values.currentStep === StudentOnlineApplicationStep.documents;

        if (isLast) {
            return;
        }

        setFieldValue('currentStep', actualStep);

        handleScrollToTop();
    };

    const isShowPreviousButton =
        values.currentStep !== StudentOnlineApplicationStep.introduction &&
        values.currentStep !== StudentOnlineApplicationStep.summary;

    const isShowNextButton = values.currentStep !== StudentOnlineApplicationStep.documents;

    const renderedNextButtonLabel = React.useMemo(() => {
        if (isShowPreviousButton) {
            return 'Продолжить';
        }

        return 'Сохранить и продолжить';
    }, [isShowPreviousButton]);

    const disabledNext = React.useMemo(() => {
        if (values.currentStep === StudentOnlineApplicationStep.introduction) {
            return !values.introduction.passed;
        }

        return false;
    }, [values.currentStep, values.introduction]);

    return (
        <Root>
            {isShowPreviousButton ? (
                <Button
                    variant='outlined'
                    disabled={isSubmitting}
                    startIcon={arrowBackIcon}
                    onClick={handlePrev}
                >
                    Назад
                </Button>
            ) : null}

            {isShowNextButton ? (
                <Button
                    disabled={disabledNext || isSubmitting}
                    endIcon={arrowForwardIcon}
                    onClick={handleNext}
                >
                    {renderedNextButtonLabel}
                </Button>
            ) : null}
        </Root>
    );
};

const Root = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export { StudentOnlineApplicationFooter };
